import { useEffect } from 'react'

export interface SEOMeta {
  title?: string
  description?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  twitterCard?: 'summary' | 'summary_large_image'
  twitterTitle?: string
  twitterDescription?: string
  noindex?: boolean
  canonical?: string
}

const DEFAULT_META = {
  title: 'Ark Design Swarm',
  description: 'Multi-surface Ark command cockpit — Portfolio Proof System, Writing Stone CanonWeaver, Job Acquisition Cockpit, Evidence Recovery, and Ark Registry.',
  ogTitle: 'Ark Design Swarm',
  ogDescription: 'Multi-surface Ark command cockpit prototype.',
  ogImage: '/ark-void.svg',
  twitterCard: 'summary' as const,
  twitterTitle: 'Ark Design Swarm',
  twitterDescription: 'Multi-surface Ark command cockpit prototype.',
}

export function setSEOMeta(meta: SEOMeta) {
  const title = meta.title || DEFAULT_META.title
  const description = meta.description || DEFAULT_META.description
  const ogTitle = meta.ogTitle || title
  const ogDescription = meta.ogDescription || description
  const ogImage = meta.ogImage || DEFAULT_META.ogImage
  const twitterCard = meta.twitterCard || DEFAULT_META.twitterCard
  const twitterTitle = meta.twitterTitle || ogTitle
  const twitterDescription = meta.twitterDescription || ogDescription

  document.title = title ? `ARK — ${title}` : DEFAULT_META.title

  const setMeta = (name: string, content: string) => {
    let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"], meta[property="${name}"]`)
    if (!el) {
      el = document.createElement('meta')
      el.setAttribute(name.startsWith('og:') || name.startsWith('twitter:') ? 'property' : 'name', name)
      document.head.appendChild(el)
    }
    el.content = content
  }

  setMeta('description', description)
  setMeta('og:title', ogTitle)
  setMeta('og:description', ogDescription)
  setMeta('og:image', ogImage)
  setMeta('og:type', 'website')
  setMeta('twitter:card', twitterCard)
  setMeta('twitter:title', twitterTitle)
  setMeta('twitter:description', twitterDescription)

  if (meta.noindex) {
    setMeta('robots', 'noindex, nofollow')
  } else {
    // Remove noindex if previously set
    const robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]')
    if (robots && robots.content.includes('noindex')) {
      robots.remove()
    }
  }

  if (meta.canonical) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'canonical'
      document.head.appendChild(link)
    }
    link.href = meta.canonical
  }
}

export function useSEOMeta(meta: SEOMeta) {
  useEffect(() => {
    setSEOMeta(meta)
  }, [meta.title, meta.description, meta.noindex])
}

export function getSurfaceMeta(surface: string, route?: string): SEOMeta {
  const base: SEOMeta = DEFAULT_META

  const surfaceDescriptions: Record<string, string> = {
    portfolio: 'Portfolio Proof System — 433 public builds, 372 owner-built core, locked by RULING_001.',
    'writing-stone': 'Writing Stone CanonWeaver — 7 contradictions, WS_004 RESOLVED_BY_SEQUENCE, 1,626 entities.',
    jobs: 'Job Acquisition Cockpit — 71 target roles, 34 companies, auto-apply disabled.',
    evidence: 'Evidence Recovery — 27 events, 0 raw sources, ALL QUOTE_BLOCKED. NOT PUBLIC PROOF.',
    registry: 'Ark Registry — 4,597 source files, 4 quarantined archives, OpenClaw staging.',
    notebooklm: 'NotebookLM Audit Station — 7 notebooks, 108 sources, AUDITOR_ONLY (not canon authority).',
  }

  const noindexSurfaces = ['evidence', 'registry']
  const noindexRoutes = ['/evidence', '/evidence/chronology', '/evidence/recovery-queue', '/evidence/badge-reference']

  const isNoindex = Boolean(noindexSurfaces.includes(surface) || (route && noindexRoutes.includes(route)))

  return {
    ...base,
    title: `${surfaceDescriptions[surface] || base.title}`,
    description: surfaceDescriptions[surface] || base.description,
    noindex: isNoindex,
    canonical: route ? `https://ark-design-swarm.pages.dev${route}` : undefined,
  }
}
